import { useState } from "react";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { toast } from "sonner";

export default function Contact() {
    const { t } = useT();
    const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/inquiries", form);
            toast.success(t("toast.inquirySent"));
            setForm({ name: "", email: "", phone: "", subject: "", message: "" });
        } catch (err) {
            toast.error(t("toast.inquiryError"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="contact-page" className="bg-bone min-h-screen">
            <Navbar />
            <section className="pt-32 lg:pt-40 pb-16 px-6 lg:px-12">
                <div className="max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16">
                    <div className="lg:col-span-5">
                        <div className="eyebrow mb-4">Contact</div>
                        <h1 className="font-chiller text-5xl lg:text-6xl leading-[1.02] tracking-tight mb-8">
                            Send a note.<br />
                            <em className="not-italic text-terracotta">We'll write back.</em>
                        </h1>
                        <div className="space-y-6 text-base text-muted2">
                            <div>
                                <div className="eyebrow mb-2">Reservations</div>
                                <div>wildcoffeeretreats@gmail.com</div>
                            </div>
                            <div>
                                <div className="eyebrow mb-2">Press & partnerships</div>
                                <div>wildcoffeeretreats@gmail.com</div>
                            </div>
                            <div>
                                <div className="eyebrow mb-2">Visit by appointment</div>
                                <div>Negril, Jamaica<br />West End Road</div>
                            </div>
                        </div>
                    </div>

                    <form onSubmit={submit} className="lg:col-span-6 lg:col-start-7 space-y-6" data-testid="contact-form">
                        <Field label="Your name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="contact-name" required />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="contact-email" required />
                            <Field label="Phone (optional)" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="contact-phone" />
                        </div>
                        <Field label="Subject" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} testid="contact-subject" />
                        <div>
                            <label className="eyebrow block mb-2">Your message *</label>
                            <textarea
                                data-testid="contact-message"
                                value={form.message}
                                onChange={(e) => setForm({ ...form, message: e.target.value })}
                                required
                                rows={6}
                                className="w-full bg-transparent border-b border-espresso/30 focus:border-forest outline-none py-3 text-base"
                            />
                        </div>
                        <button
                            type="submit"
                            data-testid="contact-submit"
                            disabled={submitting}
                            className="mt-4 inline-flex items-center gap-2 px-8 py-4 bg-forest text-bone hover:bg-forestDark transition-colors text-xs uppercase tracking-widest2 font-semibold disabled:opacity-50"
                        >
                            {submitting ? "Sending…" : "Send message"}
                        </button>
                    </form>
                </div>
            </section>

            {/* GETTING HERE */}
            <section className="border-t border-espresso/15 py-20 lg:py-28 px-6 lg:px-12 bg-bone2/30" data-testid="getting-here-section">
                <div className="max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-4">
                        <div className="eyebrow mb-4">Getting here</div>
                        <h2 className="font-chiller text-4xl lg:text-5xl leading-tight mb-8">
                            Kotch pon a Rock —<br />
                            <em className="not-italic text-forest">West End Road, Negril.</em>
                        </h2>
                        <div className="space-y-6 text-base text-muted2">
                            <div>
                                <div className="eyebrow mb-1 text-gold">By air</div>
                                <p className="leading-relaxed">Sangster International <span className="text-ink">(MBJ)</span> in Montego Bay is the closest airport — roughly <span className="text-ink">90 minutes</span> by car along the coastal A1.</p>
                            </div>
                            <div>
                                <div className="eyebrow mb-1 text-gold">By road</div>
                                <p className="leading-relaxed">Negril Aerodrome <span className="text-ink">(NEG)</span> sits 10 minutes from the property; private transfers and taxis are easy to arrange — just tell us your arrival window when you book.</p>
                            </div>
                            <div>
                                <div className="eyebrow mb-1 text-gold">On arrival</div>
                                <p className="leading-relaxed">The retreat sits on West End Road, behind unmistakable rustic stone walls. Look for the painted gate — we'll be there to meet you at check-in.</p>
                            </div>
                            <div className="pt-2">
                                <a
                                    href="https://maps.google.com/?q=West+End+Road+Negril+Jamaica"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="contact-open-maps"
                                    className="inline-flex items-center gap-2 text-xs uppercase tracking-widest2 font-semibold text-forest hover:text-terracotta"
                                >
                                    Open in Google Maps →
                                </a>
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-7 lg:col-start-6">
                        <div className="aspect-[16/11] overflow-hidden border border-espresso/20" data-testid="contact-map-frame">
                            {/** https://www.google.com/maps?q=West%20End%20Road%2C%20Negril%2C%20Jamaica&output=embed */}
                            
                            <iframe
                                title="Wild Coffee Retreat — West End Road, Negril, Jamaica"
                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d857.2853127878644!2d-78.35027510623962!3d18.240943048235017!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8ed91268a7a11555%3A0x48f0818593673f21!2sWild%20Coffee%20Retreat!5e1!3m2!1sen!2sjm!4v1792876275359!5m2!1sen!2sjm"
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                className="w-full h-full grayscale-[15%] contrast-[1.02]"
                                style={{ border: 0 }}
                                allowFullScreen
                            />
                        </div>
                        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-[11px] uppercase tracking-widest2 text-espresso/80">
                            <span>Adults only · Check-in 3:00 PM · Check-out 11:00 AM</span>
                            <span className="text-terracotta">Home of Artical people</span>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}

function Field({ label, value, onChange, type = "text", testid, required }) {
    return (
        <div>
            <label className="eyebrow block mb-2">{label}{required ? " *" : ""}</label>
            <input
                data-testid={testid}
                type={type}
                value={value}
                required={required}
                onChange={(e) => onChange(e.target.value)}
                className="w-full bg-transparent border-b border-espresso/30 focus:border-forest outline-none py-3 text-base"
            />
        </div>
    );
}
